import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import PendingOutlined from '@mui/icons-material/PendingOutlined';
import Sync from '@mui/icons-material/Sync';

const BAR_GRADIENT = 'linear-gradient(90deg, #2dd4a3 0%, #4ce1b6 45%, #7ef0d4 100%)';

/**
 * Fancy progress UI for competitor discover + bulk sync (rates / profiles).
 */
export default function CompetitorSyncProgressDialog({
  open,
  title = 'Sync in progress',
  steps = [],
  footerNote,
  errorMessage,
  completed = false,
  isDark = false,
  onClose,
  /** Live job: current index (1-based), total competitors, optional name */
  itemProgress = null,
}) {
  const ip = itemProgress && itemProgress.total > 0 ? itemProgress : null;
  const itemPct = ip ? Math.round((ip.current / ip.total) * 100) : 0;

  return (
    <Dialog
      open={open}
      onClose={completed ? onClose : undefined}
      disableEscapeKeyDown={!completed}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: isDark
            ? 'linear-gradient(145deg, #252530 0%, #1e1e24 100%)'
            : 'linear-gradient(145deg, #ffffff 0%, #f8fafb 100%)',
          border: `1px solid ${isDark ? alpha('#4ce1b6', 0.35) : alpha('#4ce1b6', 0.5)}`,
          boxShadow: isDark
            ? `0 24px 48px rgba(0,0,0,0.45), 0 0 0 1px ${alpha('#4ce1b6', 0.12)}`
            : `0 24px 48px rgba(15, 23, 42, 0.12), 0 0 0 1px ${alpha('#4ce1b6', 0.15)}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          fontWeight: 800,
          letterSpacing: '-0.02em',
          color: isDark ? '#fff' : '#0f172a',
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {ip ? (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              bgcolor: isDark ? alpha('#4ce1b6', 0.08) : alpha('#4ce1b6', 0.1),
              border: `1px solid ${alpha('#4ce1b6', 0.28)}`,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
              <Typography variant="caption" fontWeight={800} sx={{ color: isDark ? alpha('#fff', 0.85) : 'text.primary' }}>
                Current job
              </Typography>
              <Typography
                variant="caption"
                fontWeight={800}
                sx={{ fontVariantNumeric: 'tabular-nums', color: '#4ce1b6' }}
              >
                {ip.current} / {ip.total}
              </Typography>
            </Stack>
            {ip.label ? (
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ mb: 1, color: isDark ? alpha('#fff', 0.9) : 'text.primary', lineHeight: 1.35 }}
                noWrap
                title={ip.label}
              >
                {ip.label}
              </Typography>
            ) : null}
            <LinearProgress
              variant="determinate"
              value={itemPct}
              sx={{
                height: 8,
                borderRadius: 999,
                bgcolor: isDark ? alpha('#000', 0.35) : alpha('#e2e8f0', 0.95),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  background: BAR_GRADIENT,
                },
              }}
            />
          </Box>
        ) : null}

        <Stack spacing={1.75}>
          {steps.map((step) => {
            const { id, label, status, sublabel } = step;
            const done = status === 'done';
            const running = status === 'running';
            const err = status === 'error';
            return (
              <Stack
                key={id}
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: running
                    ? isDark
                      ? alpha('#4ce1b6', 0.1)
                      : alpha('#4ce1b6', 0.12)
                    : isDark
                      ? alpha('#fff', 0.04)
                      : alpha('#64748b', 0.06),
                  border: `1px solid ${
                    running
                      ? alpha('#4ce1b6', 0.45)
                      : isDark
                        ? alpha('#fff', 0.08)
                        : alpha('#64748b', 0.12)
                  }`,
                  transition: 'background-color 0.25s ease, border-color 0.25s ease',
                }}
              >
                <Box sx={{ pt: 0.125, color: done ? '#4ce1b6' : err ? 'error.main' : alpha('#4ce1b6', running ? 1 : 0.45) }}>
                  {done ? (
                    <CheckCircle sx={{ fontSize: 26 }} />
                  ) : err ? (
                    <ErrorOutline sx={{ fontSize: 26 }} />
                  ) : running ? (
                    <Sync
                      sx={{
                        fontSize: 26,
                        animation: 'nexaSpin 1.1s linear infinite',
                        '@keyframes nexaSpin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
                      }}
                    />
                  ) : (
                    <PendingOutlined sx={{ fontSize: 26, opacity: 0.5 }} />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ color: isDark ? '#f1f5f9' : '#0f172a', lineHeight: 1.35 }}
                  >
                    {label}
                  </Typography>
                  {sublabel ? (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.35, color: isDark ? alpha('#fff', 0.55) : 'text.secondary' }}>
                      {sublabel}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
            );
          })}
        </Stack>

        {footerNote ? (
          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: isDark ? alpha('#fff', 0.5) : 'text.secondary', lineHeight: 1.5 }}>
            {footerNote}
          </Typography>
        ) : null}

        {errorMessage ? (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              bgcolor: isDark ? alpha('#f87171', 0.12) : alpha('#ef4444', 0.08),
              border: `1px solid ${alpha('#f87171', 0.35)}`,
            }}
          >
            <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
              {errorMessage}
            </Typography>
          </Box>
        ) : null}
      </DialogContent>
      {completed ? (
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 3,
              bgcolor: '#4ce1b6',
              color: '#0d1f1a',
              '&:hover': { bgcolor: '#3dd4a8' },
            }}
          >
            Done
          </Button>
        </DialogActions>
      ) : null}
    </Dialog>
  );
}
